import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";

// Lazy-loaded pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const DigitalReceipt = lazy(() => import("./pages/DigitalReceipt"));
const DigitalShowcase = lazy(() => import("./pages/DigitalShowcase"));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy-loaded authenticated app shell (includes heavy providers + all protected routes)
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Lightweight public routes — no heavy providers */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/receipt" element={<DigitalReceipt />} />
            <Route path="/showcase" element={<DigitalShowcase />} />
            <Route path="/showcase/:userId" element={<DigitalShowcase />} />
            <Route path="/portal/:shopId" element={<CustomerPortal />} />

            {/* All other routes go through AuthenticatedApp (lazy-loaded with providers) */}
            <Route path="/*" element={<AuthenticatedApp />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
